import { z } from "zod";
import { DateSchema, MarketplaceOfferingId } from "../Types";
export const MarketplaceAssetType = z.enum([
    "insurance",
    "lending",
    "alternative_asset",
]);
export const InsuranceOfferingType = z.enum([
    "term_life",
    "permanent_life",
    "annuities",
    "long-term_care",
]);
export const LendingOfferingType = z.enum([
    "home_loans",
    "securities-based_loans",
    "commercial_loans",
    "specialty_loans",
    "personal_loans",
]);
export const AltAssetOfferingType = z.enum([
    "private_equity",
    "private_debt",
    "hedge_fund",
    "venture_capital",
    "collectibles",
    "digital_assets",
    "real_assets",
    "structured_investments",
]);
export const MarketplaceOfferingType = z.union([
    InsuranceOfferingType,
    LendingOfferingType,
    AltAssetOfferingType,
]);
const MarketplaceInsuranceOfferingTypes = z.object({
    assetType: z.literal(MarketplaceAssetType.enum.insurance),
    offeringType: InsuranceOfferingType,
});
const MarketplaceLendingOfferingTypes = z.object({
    assetType: z.literal(MarketplaceAssetType.enum.lending),
    offeringType: LendingOfferingType,
});
const MarketplaceAltAssetOfferingTypes = z.object({
    assetType: z.literal(MarketplaceAssetType.enum.alternative_asset),
    offeringType: AltAssetOfferingType,
});
export const MarketplaceOfferingTagType = z.enum([
    "string",
    "number",
    "iso8601",
    "boolean",
    "select",
    "currency_usd",
]);
export const MarketplaceOfferingTagFormat = z.enum([
    "only_value",
    "reverse_short",
]);
export const MarketplaceOfferingTag = z.object({
    key: z.string(),
    keyShortDisplayName: z.string().nullable(),
    value: z.string(),
    type: MarketplaceOfferingTagType,
    format: MarketplaceOfferingTagFormat,
});
const MarketplaceOfferingBase = z.object({
    offeringId: MarketplaceOfferingId,
    partnerName: z.string(),
    about: z.string(),
    howItWorks: z.string(),
    isFeatured: z.boolean(),
    createdTs: DateSchema,
    modifiedTs: DateSchema,
});
const MarketplaceOfferingBaseV2 = MarketplaceOfferingBase.extend({
    tags: MarketplaceOfferingTag.array(),
});
export const MarketplaceInsuranceOffering = z.object({
    v1: MarketplaceOfferingBase.merge(MarketplaceInsuranceOfferingTypes),
    v2: MarketplaceOfferingBaseV2.merge(MarketplaceInsuranceOfferingTypes),
});
export const MarketplaceLendingOffering = z.object({
    v1: MarketplaceOfferingBase.merge(MarketplaceLendingOfferingTypes),
    v2: MarketplaceOfferingBaseV2.merge(MarketplaceLendingOfferingTypes),
});
export const MarketplaceAltAssetOffering = z.object({
    v1: MarketplaceOfferingBase.merge(MarketplaceAltAssetOfferingTypes),
    v2: MarketplaceOfferingBaseV2.merge(MarketplaceAltAssetOfferingTypes),
});
export const MarketplaceOffering = z.object({
    v1: z.union([
        MarketplaceInsuranceOffering.shape.v1,
        MarketplaceLendingOffering.shape.v1,
        MarketplaceAltAssetOffering.shape.v1,
    ]),
    v2: z.union([
        MarketplaceInsuranceOffering.shape.v2,
        MarketplaceLendingOffering.shape.v2,
        MarketplaceAltAssetOffering.shape.v2,
    ]),
});
export const MarketplaceOfferingPartner = z.object({
    name: z.string(),
    description: z.string(),
    createdTs: DateSchema,
    modifiedTs: DateSchema,
});
export const MarketplaceOfferingContact = z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    calendlyLink: z.string().nullable(),
    createdTs: DateSchema,
    modifiedTs: DateSchema,
});
export const MarketplaceOfferingAttachment = z.object({
    id: z.string().uuid(),
    previewUrl: z.string().url(),
    downloadUrl: z.string().url(),
    filename: z.string(),
    createdTs: DateSchema,
    modifiedTs: DateSchema,
});
export const MakretplaceOfferingDataType = z.enum([
    "section",
    ...MarketplaceOfferingTagType.options,
]);
const MarketplaceOfferingDataBase = z.object({
    key: z.string(),
    keyDisplayName: z.string(),
    type: MakretplaceOfferingDataType,
});
// NOTE: this is a recursive schema
export const MarketplaceOfferingData = MarketplaceOfferingDataBase.extend({
    value: z.lazy(() => z.array(z.union([MarketplaceOfferingData, z.string()]))),
});
export const MarketplaceOfferingDetailsBase = z.object({
    offeringId: MarketplaceOfferingId,
    partner: MarketplaceOfferingPartner,
    howItWorks: z.string(),
    otherOfferings: z.string().array(),
    providers: z.string().array(),
    isFeatured: z.boolean(),
    isDeleted: z.boolean(),
    createdTs: DateSchema,
    modifiedTs: DateSchema,
});
export const MarketplaceOfferingDetailsBaseV2 = MarketplaceOfferingDetailsBase.omit({
    otherOfferings: true,
    providers: true,
}).extend({
    tags: MarketplaceOfferingTag.array(),
    offeringData: MarketplaceOfferingData.array(),
});
const MarketplaceOfferingDetailsBaseClientExtension = z.object({
    hasUserExpressedInterest: z.boolean(),
});
export const MarketplaceOfferingDetailsBaseClient = z.object({
    v1: MarketplaceOfferingDetailsBase.merge(MarketplaceOfferingDetailsBaseClientExtension),
    v2: MarketplaceOfferingDetailsBaseV2.merge(MarketplaceOfferingDetailsBaseClientExtension),
});
const MarketplaceOfferingDetailsBaseAdvisorExtensionV1 = z.object({
    contact: MarketplaceOfferingContact,
    howToGetStarted: z.string(),
    website: z.string().nullable(),
    selfServiceLink: z.string().nullable(),
    attachments: MarketplaceOfferingAttachment.array(),
});
const MarketplaceOfferingDetailsBaseAdvisorExtensionV2 = z.object({
    contact: MarketplaceOfferingContact,
    howToGetStarted: z.string(),
    selfServiceLink: z.string().nullable(),
    resources: z.object({
        website: z.string().nullable(),
        attachments: MarketplaceOfferingAttachment.array(),
    }),
});
export const MarketplaceOfferingDetailsBaseAdvisor = z.object({
    v1: MarketplaceOfferingDetailsBase.merge(MarketplaceOfferingDetailsBaseAdvisorExtensionV1),
    v2: MarketplaceOfferingDetailsBaseV2.merge(MarketplaceOfferingDetailsBaseAdvisorExtensionV2),
});
export const MarketplaceInsuranceOfferingDetailsClient = z.object({
    v1: MarketplaceOfferingDetailsBaseClient.shape.v1.merge(MarketplaceInsuranceOfferingTypes),
    v2: MarketplaceOfferingDetailsBaseClient.shape.v2.merge(MarketplaceInsuranceOfferingTypes),
});
export const MarketplaceLendingOfferingDetailsClient = z.object({
    v1: MarketplaceOfferingDetailsBaseClient.shape.v1.merge(MarketplaceLendingOfferingTypes),
    v2: MarketplaceOfferingDetailsBaseClient.shape.v2.merge(MarketplaceLendingOfferingTypes),
});
export const MarketplaceAltAssetOfferingDetailsClient = z.object({
    v1: MarketplaceOfferingDetailsBaseClient.shape.v1.merge(MarketplaceAltAssetOfferingTypes),
    v2: MarketplaceOfferingDetailsBaseClient.shape.v2.merge(MarketplaceAltAssetOfferingTypes),
});
export const MarketplaceOfferingDetailsClient = z.object({
    v1: z.union([
        MarketplaceInsuranceOfferingDetailsClient.shape.v1,
        MarketplaceLendingOfferingDetailsClient.shape.v1,
        MarketplaceAltAssetOfferingDetailsClient.shape.v1,
    ]),
    v2: z.union([
        MarketplaceInsuranceOfferingDetailsClient.shape.v2,
        MarketplaceLendingOfferingDetailsClient.shape.v2,
        MarketplaceAltAssetOfferingDetailsClient.shape.v2,
    ]),
});
export const MarketplaceInsuranceOfferingDetailsAdvisor = z.object({
    v1: MarketplaceOfferingDetailsBaseAdvisor.shape.v1.merge(MarketplaceInsuranceOfferingTypes),
    v2: MarketplaceOfferingDetailsBaseAdvisor.shape.v2.merge(MarketplaceInsuranceOfferingTypes),
});
export const MarketplaceLendingOfferingDetailsAdvisor = z.object({
    v1: MarketplaceOfferingDetailsBaseAdvisor.shape.v1.merge(MarketplaceLendingOfferingTypes),
    v2: MarketplaceOfferingDetailsBaseAdvisor.shape.v2.merge(MarketplaceLendingOfferingTypes),
});
export const MarketplaceAltAssetOfferingDetailsAdvisor = z.object({
    v1: MarketplaceOfferingDetailsBaseAdvisor.shape.v1.merge(MarketplaceAltAssetOfferingTypes),
    v2: MarketplaceOfferingDetailsBaseAdvisor.shape.v2.merge(MarketplaceAltAssetOfferingTypes),
});
export const MarketplaceOfferingDetailsAdvisor = z.object({
    v1: z.union([
        MarketplaceInsuranceOfferingDetailsAdvisor.shape.v1,
        MarketplaceLendingOfferingDetailsAdvisor.shape.v1,
        MarketplaceAltAssetOfferingDetailsAdvisor.shape.v1,
    ]),
    v2: z.union([
        MarketplaceInsuranceOfferingDetailsAdvisor.shape.v2,
        MarketplaceLendingOfferingDetailsAdvisor.shape.v2,
        MarketplaceAltAssetOfferingDetailsAdvisor.shape.v2,
    ]),
});
//# sourceMappingURL=Types.js.map